import clsx from 'clsx'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { Fragment } from 'react'
import { TypographyLabel } from '../typography'

const Tabs = ({
	tabType,
	defaultTab,
	tabOptions,
	onChange,
	additionalClasses = '',
}: {
	tabType: string
	defaultTab: number
	tabOptions: [
		{
			title: string
			titleCaption?: string
			leftIcon?: any
			rightIcon?: any
			child?: any
			disabled?: boolean
		},
	]
	onChange: any
	additionalClasses: string
}) => {
	// By default show PillTab
	let classes = clsx(
		'flex items-center',
		'py-1 px-4 text-labelLargeBold text-textPrimaryLight rounded-full border-solid border border-textSecondaryLight',
		'aria-selected:bg-grey800 aria-selected:text-textPrimaryDark',
		'disabled:text-textInactiveLight disabled:border-transparent',
		'hover:border-textPrimaryLight',
		'dark:border-textSecondaryDark dark:text-textPrimaryDark',
		'dark:hover:border-textPrimaryDark',
		'dark:aria-selected:bg-white dark:aria-selected:text-rulePrimaryDark',
		'dark:disabled:border-transparent dark:disabled:text-textInactiveDark',
		additionalClasses,
	)

	// Tab large
	if (tabType == 'DefaultLargeBordered') {
		classes = clsx(
			'flex items-center focus-visible:outline-none -mb-[2px] cursor-pointer',
			'pt-1 pb-2 px-6 text-labelLargeBold text-textSecondaryLight border-b-2 border-dotted border-textSecondaryLight',
			'aria-selected:text-textPrimaryLight aria-selected:border-textPrimaryLight aria-selected:border-b-2 aria-selected:border-solid',
			'disabled:text-textInactiveLight disabled:border-transparent',
			'hover:border-textPrimaryLight hover:border-solid',
			'dark:border-textSecondaryDark dark:text-textSecondaryDark',
			'dark:hover:border-textPrimaryDark',
			'dark:aria-selected:text-textPrimaryDark dark:aria-selected:border-textPrimaryDark dark:aria-selected:border-b-2 dark:aria-selected:border-solid',
			'dark:disabled:border-transparent dark:disabled:text-textInactiveDark',
			additionalClasses,
		)
	}

	// Tab large
	if (tabType == 'DefaultLarge') {
		classes = clsx(
			'flex items-center focus-visible:outline-none',
			'py-1 px-6 text-labelLargeBold text-textSecondaryLight border-dotted border-b-2 border-textSecondaryLight',
			'aria-selected:text-textPrimaryLight aria-selected:border-textPrimaryLight aria-selected:border-b-2 aria-selected:border-solid',
			'disabled:text-textInactiveLight disabled:border-transparent',
			'hover:border-textPrimaryLight hover:border-solid',
			'dark:border-textSecondaryDark dark:text-textSecondaryDark',
			'dark:hover:border-textPrimaryDark',
			'dark:aria-selected:text-textPrimaryDark dark:aria-selected:border-textPrimaryDark dark:aria-selected:border-b-2 dark:aria-selected:border-solid',
			'dark:disabled:border-transparent dark:disabled:text-textInactiveDark',
			additionalClasses,
		)
	}

	// Tab medium
	if (tabType == 'DefaultMedium') {
		classes = clsx(
			'flex items-center',
			'sticky top-0 z-10',
			'py-1 text-labelMediumBold text-textSecondaryLight border-dotted border-b-2 border-textSecondaryLight',
			'aria-selected:text-textPrimaryLight aria-selected:border-textPrimaryLight aria-selected:border-b-2 aria-selected:border-solid',
			'disabled:text-textInactiveLight disabled:border-transparent',
			'hover:border-textPrimaryLight hover:border-solid',
			'dark:border-textSecondaryDark dark:text-textSecondaryDark',
			'dark:hover:border-textPrimaryDark',
			'dark:aria-selected:text-textPrimaryDark dark:aria-selected:border-textPrimaryDark dark:aria-selected:border-b-2 dark:aria-selected:border-solid',
			'dark:disabled:border-transparent dark:disabled:text-textInactiveDark',
			additionalClasses,
		)
	}

	// Tab small
	if (tabType == 'DefaultSmall') {
		classes = clsx(
			'flex items-center',
			'py-1 text-labelSmallBold text-textSecondaryLight border-dotted border-b-2 border-textSecondaryLight',
			'aria-selected:text-textPrimaryLight aria-selected:border-textPrimaryLight aria-selected:border-b-2 aria-selected:border-solid',
			'disabled:text-textInactiveLight disabled:border-transparent',
			'hover:border-textPrimaryLight hover:border-solid',
			'dark:border-textSecondaryDark dark:text-textSecondaryDark',
			'dark:hover:border-textPrimaryDark',
			'dark:aria-selected:text-textPrimaryDark dark:aria-selected:border-textPrimaryDark dark:aria-selected:border-b-2 dark:aria-selected:border-solid',
			'dark:disabled:border-transparent dark:disabled:text-textInactiveDark',
			additionalClasses,
		)
	}

	// Vertical tab
	if (tabType == 'Vertical') {
		classes = clsx(
			'flex items-center cursor-pointer',
			'w-full py-1 text-labelMediumBold text-textSecondaryLight border-dotted border-l-2 border-textSecondaryLight',
			'aria-selected:text-textPrimaryLight aria-selected:bg-uiTertiaryLight aria-selected:text-textPrimaryLight aria-selected:border-solid',
			'disabled:text-textInactiveLight disabled:border-transparent',
			'hover:border-textPrimaryLight hover:border-solid hover:bg-uiTertiaryLight hover:text-textSecondaryLight',
			'dark:border-textSecondaryDark dark:text-textSecondaryDark',
			'dark:hover:border-textSecondaryDark dark:hover:bg-uiTertiaryDark dark:hover:text-textSecondaryDark',
			'dark:aria-selected:text-textPrimaryDark dark:aria-selected:bg-uiTertiaryDark dark:aria-selected:border-textPrimaryDark dark:aria-selected:border-l-2 dark:aria-selected:border-solid',
			'dark:disabled:border-textInactiveDark dark:disabled:border-l-2 dark:disabled:border-solid dark:disabled:text-textInactiveDark',
			'dark:disabled:hover:border-textInactiveDark',
			'outline-none ring-0',
			additionalClasses,
		)
	}

	return (
		<TabGroup selectedIndex={defaultTab} as={Fragment} onChange={onChange}>
			<div
				className={
					tabType.toLowerCase().includes('vertical')
						? 'flex w-full'
						: 'w-full flex-col'
				}
			>
				<TabList
					className={
						tabType.toLowerCase().includes('vertical')
							? 'sticky top-0 z-20 inline-block h-min w-1/4 rounded-xl px-1 pb-2'
							: tabType === 'DefaultLargeBordered'
								? 'sticky top-0 z-20 flex h-min rounded-none bg-white px-1 pb-2 dark:bg-dark'
								: 'sticky top-0 z-20 flex h-min rounded-xl bg-white px-1 pb-2 dark:bg-dark'
					}
				>
					{tabOptions.map(tabOption => (
						<Tab
							disabled={tabOption.disabled ? true : false}
							key={tabOption.title}
							as={Fragment}
						>
							{({ selected }) => (
								<div
									className={clsx(
										classes,
										'focus-visible:outline-none',
										tabType.toLowerCase().includes('vertical') ? '' : 'flex-1',
									)}
								>
									{tabOption.leftIcon && (
										<div className="ml-2 mr-1 flex h-full items-center">
											{tabOption.leftIcon}
										</div>
									)}
									<TypographyLabel
										size="medium"
										isBold={false}
										additionalClasses={
											tabType.toLowerCase().includes('vertical')
												? 'ml-1 my-2'
												: 'ml-1'
										}
									>
										{tabOption.title}
										{tabOption.titleCaption ? (
											<span className="font-abc text-labelMedium text-textSecondaryLight dark:text-textSecondaryDark">
												{tabOption.titleCaption}
											</span>
										) : (
											''
										)}
									</TypographyLabel>
									{tabOption.rightIcon && (
										<div className="ml-auto mr-0 flex h-full items-center">
											{tabOption.rightIcon}
										</div>
									)}
								</div>
							)}
						</Tab>
					))}
				</TabList>
				<TabPanels
					className={tabType == 'Vertical' ? 'mt-2 md:w-3/4' : 'mt-2 w-full'}
				>
					{tabOptions.map(tabOption => (
						<TabPanel key={tabOption.title}>{tabOption.child}</TabPanel>
					))}
				</TabPanels>
			</div>
		</TabGroup>
	)
}

export default Tabs
